import { useMemo, useState } from "react";
import type { TapWordBlock } from "@shared/lessonBlocks";
import { RichText } from "../RichText";
import { useCheck, CheckFooter, type CheckResult } from "../CheckShell";

interface Props {
  block: TapWordBlock;
  onResult: (r: CheckResult) => void;
  alreadyDone?: CheckResult | null;
}

const norm = (w: string) => w.replace(/[^\p{L}\p{N}'’-]/gu, "").toLowerCase();

/** Tap the words that match the instruction (e.g. "Tap every noun"). */
export function TapWordBlockView({ block, onResult, alreadyDone }: Props) {
  const check = useCheck(onResult, { alreadyDone });
  const tokens = useMemo(() => block.sentence.split(/\s+/).filter(Boolean), [block.sentence]);
  const targets = useMemo(() => new Set(block.targets.map(norm)), [block.targets]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [wrong, setWrong] = useState<Set<number>>(new Set());
  const done = check.locked;

  const toggle = (i: number) => {
    if (done) return;
    if (check.status === "wrong") check.retry();
    setSelected(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
    setWrong(prev => { const n = new Set(prev); n.delete(i); return n; });
  };
  const submit = () => {
    const bad = new Set<number>();
    tokens.forEach((t, i) => {
      const should = targets.has(norm(t));
      if (should !== selected.has(i)) bad.add(i);
    });
    setWrong(bad);
    check.submit(bad.size === 0);
  };
  const revealed = done && check.status === "revealed";

  return (
    <div className="space-y-4">
      <RichText text={block.prompt} className="text-lg md:text-xl font-semibold" />
      <div className="rounded-2xl border border-border bg-card p-4 flex flex-wrap gap-x-1.5 gap-y-2 text-xl leading-relaxed">
        {tokens.map((t, i) => {
          const isTarget = targets.has(norm(t));
          const isSel = revealed ? isTarget : selected.has(i);
          const isWrong = wrong.has(i) && !done;
          const cls = done
            ? (isTarget && isSel ? "bg-emerald-200 dark:bg-emerald-800/60 ring-2 ring-emerald-400" : isSel ? "bg-rose-200 dark:bg-rose-800/60 line-through" : isTarget ? "ring-2 ring-amber-400 bg-amber-100 dark:bg-amber-900/40" : "")
            : isWrong ? "bg-rose-200 dark:bg-rose-800/60 ring-2 ring-rose-400"
            : isSel ? "bg-primary text-primary-foreground shadow"
            : "hover:bg-muted";
          return (
            <button
              key={i}
              type="button"
              disabled={done}
              onClick={() => toggle(i)}
              className={`rounded-lg px-1.5 py-0.5 font-medium transition-all active:scale-95 ${cls}`}
              data-testid={`tapword-${i}`}
            >
              {t}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">{selected.size} selected</p>
      <CheckFooter
        check={check}
        canSubmit={selected.size > 0}
        onSubmit={submit}
        feedbackWrong={wrong.size ? "Some words are wrong or missing. Red ones are mistakes." : undefined}
        revealAnswer={<p>The correct words are highlighted in green.</p>}
      />
    </div>
  );
}
